import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { BugAIAnalysisPanel } from '@/components/BugAIAnalysisPanel';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useBugReportStore } from '@/stores/bugReportStore';
import { useAuthStore } from '@/stores/authStore';
import { useServiceAccess } from '@/hooks/useServiceAccess';
import { useImpersonationStore } from '@/stores/impersonationStore';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, Camera, X, Pencil } from 'lucide-react';
import { ScreenshotAnnotator } from '@/components/bug-report/ScreenshotAnnotator';

// Fullscreen annotator lightbox - outside Dialog's focus trap
function AnnotatorLightbox({
  src,
  onClose,
  onAnnotatedScreenshot,
}: {
  readonly src: string;
  readonly onClose: () => void;
  readonly onAnnotatedScreenshot: (dataUrl: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.focus();
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    document.addEventListener('keydown', handleKey, true);
    return () => document.removeEventListener('keydown', handleKey, true);
  }, [onClose]);

  return (
    <div
      ref={ref}
      tabIndex={-1}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85"
      style={{ pointerEvents: 'auto' }}
    >
      <button
        className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors z-10"
        onMouseDown={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Fermer"
      >
        <X className="h-6 w-6" />
      </button>

      <div className="w-[90vw] max-w-5xl max-h-[90vh]">
        <ScreenshotAnnotator
          screenshotSrc={src}
          onAnnotatedScreenshot={onAnnotatedScreenshot}
          fullscreen
        />
      </div>
    </div>
  );
}

interface BugReportModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly currentView: string | null;
}

const PRIORITIES = [
  { value: 'blocking', label: '🔴 Bloquant' },
  { value: 'important', label: '🟠 Important' },
  { value: 'minor', label: '🟢 Mineur' },
] as const;

const CATEGORIES = [
  { value: 'ui', label: 'UI / Affichage' },
  { value: 'data', label: 'Données' },
  { value: 'performance', label: 'Performance' },
  { value: 'navigation', label: 'Navigation' },
  { value: 'other', label: 'Autre' },
] as const;

export function BugReportModal({ open, onOpenChange, currentView }: BugReportModalProps) {
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<'blocking' | 'important' | 'minor'>('minor');
  const [category, setCategory] = useState<'ui' | 'data' | 'performance' | 'navigation' | 'other'>('ui');
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [submittedBugId, setSubmittedBugId] = useState<string | null>(null);
  const [reporterName, setReporterName] = useState<string | null>(null);
  const [reporterRoles, setReporterRoles] = useState<string[]>([]);

  const { userId } = useAuthStore();
  const { serviceAccess } = useServiceAccess();
  const { isImpersonating, impersonatedUser } = useImpersonationStore();
  const { isSubmitting, submitReport, preCapturedScreenshot, clearScreenshot } = useBugReportStore();
  const [annotatedScreenshot, setAnnotatedScreenshot] = useState<string | null>(null);

  const userRoles = Object.keys(serviceAccess).filter(s => serviceAccess[s]);

  // Fetch reporter name and roles
  useEffect(() => {
    if (!userId || !open) return;
    (async () => {
      const [{ data: userData }, { data: rolesData }] = await Promise.all([
        supabase.from('users').select('first_name, last_name').eq('id', userId).single(),
        supabase.from('user_roles').select('role').eq('user_id', userId),
      ]);
      if (userData) {
        setReporterName(`${userData.first_name || ''} ${userData.last_name || ''}`.trim() || null);
      }
      if (rolesData) {
        setReporterRoles(rolesData.map((r) => r.role));
      }
    })();
  }, [userId, open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setShowFullPreview(false);
      setSubmittedBugId(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!description.trim() || !userId) return;

    const bugId = await submitReport({
      reporterId: userId,
      description: description.trim(),
      priority,
      category,
      currentView,
      userRoles,
      isImpersonating,
      impersonatedUserId: impersonatedUser?.id || null,
      annotatedScreenshotDataUrl: annotatedScreenshot,
    });

    if (bugId) {
      toast.success('Bug signalé avec succès !');
      setSubmittedBugId(bugId);
      // Don't close — show AI analysis panel
    } else {
      toast.error('Erreur lors de l\'envoi du rapport.');
    }
  };

  const handleCloseAfterAnalysis = () => {
    setDescription('');
    setPriority('minor');
    setCategory('ui');
    clearScreenshot();
    setSubmittedBugId(null);
    onOpenChange(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>🐛 Fixer un Bug</DialogTitle>
          <DialogDescription>
            {submittedBugId
              ? 'Claude analyse le bug et le code source...'
              : currentView || globalThis.location.pathname}
          </DialogDescription>
        </DialogHeader>

        {submittedBugId ? (
          /* AI Analysis Phase */
          <BugAIAnalysisPanel
            bugReportId={submittedBugId}
            onClose={handleCloseAfterAnalysis}
          />
        ) : (
          /* Bug Report Form */
          <>
            <div className="space-y-4">
              {/* Screenshot preview */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <Camera className="h-3.5 w-3.5" />
                  Capture d'écran
                </Label>
                <div className="rounded-md border border-border overflow-hidden bg-muted/30">
                  {preCapturedScreenshot ? (
                    <div className="relative group">
                      <button
                        type="button"
                        onClick={() => setShowFullPreview(true)}
                        className="w-full h-auto max-h-48 cursor-pointer hover:opacity-80 transition-opacity bg-transparent border-0 p-0"
                        aria-label="Cliquer pour annoter"
                        title="Cliquer pour annoter"
                      >
                        <img
                          src={annotatedScreenshot || preCapturedScreenshot}
                          alt="Capture d'écran de la page"
                          className="w-full h-auto max-h-48 object-contain"
                        />
                      </button>
                      <div className="absolute bottom-2 right-2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => setShowFullPreview(true)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground shadow-sm"
                        >
                          <Pencil className="h-3 w-3" />
                          Annoter
                        </button>
                        <button
                          type="button"
                          onClick={() => { clearScreenshot(); setAnnotatedScreenshot(null); }}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md bg-destructive text-destructive-foreground shadow-sm"
                        >
                          <X className="h-3 w-3" />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
                      Capture non disponible
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bug-description">Description *</Label>
                <Textarea
                  id="bug-description"
                  placeholder="Décrivez le problème rencontré..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Priorité</Label>
                  <Select value={priority} onValueChange={(v) => setPriority(v as typeof priority)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Select value={category} onValueChange={(v) => setCategory(v as typeof category)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Context info: device, role, user */}
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2 space-y-1">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium">Déclarant :</span>{' '}
                  {reporterName || 'Chargement…'}
                  {isImpersonating && impersonatedUser
                    ? ` (impersonation de ${impersonatedUser.first_name || ''} ${impersonatedUser.last_name || ''})`.trim()
                    : ''}
                </p>
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium">Rôle(s) :</span>{' '}
                  {reporterRoles.length > 0 ? reporterRoles.join(', ') : 'Aucun'}
                </p>
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium">Device :</span>{' '}
                  {`${window.innerWidth}×${window.innerHeight} — ${/Mobile|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop'}`}
                </p>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                Annuler
              </Button>
              <Button onClick={handleSubmit} disabled={!description.trim() || isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  'Envoyer'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>

  {/* Fullscreen screenshot lightbox - rendered via portal, fully independent from Dialog */}
    {showFullPreview && preCapturedScreenshot && createPortal(
      <AnnotatorLightbox
        src={preCapturedScreenshot}
        onClose={() => setShowFullPreview(false)}
        onAnnotatedScreenshot={setAnnotatedScreenshot}
      />,
      document.body
    )}
    </>
  );
}
